import { Component, h, Host, Event, EventEmitter, State, Element } from '@stencil/core';

@Component({
  tag: 'spw-accordion-item',
  styleUrl: 'spw-accordion-item.scss',
  shadow: true,
})
export class SpwAccordionItem {
  @State() isOpen: boolean = false;
  @State() isMobile: boolean = window.innerWidth < 768;
  @Element() el: HTMLSpwAccordionItemElement;

  /** Transmet l'état actuel (isOpen) et l'id de l'élément */
  @Event() accordionToggle: EventEmitter<{ isOpen: boolean; id: string }>;

  private resizeListener: () => void;

  componentWillLoad() {
    this.assignItemId();
    this.setupResizeListener();
    this.toggleClasses();
  }

  disconnectedCallback() {
    this.teardownResizeListener();
  }

  private assignItemId() {
    const index = Array.from(this.el.parentNode.children).indexOf(this.el);
    this.el.setAttribute('data-accordion-id', `accordion-item-${index}`);
  }

  private toggleContent() {
    this.isOpen = !this.isOpen;
    const itemId = this.el.getAttribute('data-accordion-id');
    this.accordionToggle.emit({ isOpen: this.isOpen, id: itemId });
    this.updateFocusableElements();
    if (!this.isOpen) {
      this.scrollToElement();
    }
    this.toggleClasses();
  }

  private toggleClasses(): void {
    const contentElement = this.el.querySelector('spw-accordion-content');
    const titleElement = this.el.querySelector('spw-accordion-title');
    if (contentElement) {
      contentElement.classList.toggle('--collapsed', !this.isOpen);
    }
    if (titleElement) {
      titleElement.classList.toggle('--collapsed', !this.isOpen);
    }
  }

  private handleClick(event: MouseEvent) {
    event.preventDefault();
    this.toggleContent();
  }

  private scrollToElement() {
    const element = this.el.shadowRoot.querySelector('.spw-accordion-item__content') as HTMLElement;
    if (element && this.isMobile) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private updateFocusableElements() {
    const contentSlot = this.el.shadowRoot.querySelector('slot[name="content"]') as HTMLSlotElement;
    if (!contentSlot) {
      return;
    }

    const assignedElements = contentSlot.assignedElements({ flatten: true }) as HTMLElement[];

    assignedElements.forEach(element => {
      const focusableElements = element.querySelectorAll<HTMLElement>('*');

      focusableElements.forEach(focusable => {
        if (this.isOpen) {
          const originalTabIndex = focusable.getAttribute('data-tabindex-original');
          if (originalTabIndex !== null) {
            focusable.setAttribute('tabindex', originalTabIndex);
            focusable.removeAttribute('data-tabindex-original');
          } else {
            focusable.removeAttribute('tabindex');
          }
        } else {
          if (!focusable.hasAttribute('data-tabindex-original')) {
            const currentTabIndex = focusable.getAttribute('tabindex');
            if (currentTabIndex !== null) {
              focusable.setAttribute('data-tabindex-original', currentTabIndex);
            } else {
              focusable.setAttribute('data-tabindex-original', '');
            }
          }
          focusable.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  private setupResizeListener() {
    this.resizeListener = () => {
      this.isMobile = window.innerWidth < 768;
    };

    window.addEventListener('resize', this.resizeListener);
  }

  private teardownResizeListener() {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-accordion-item': true,
      'spw-accordion-item--collapsed': !this.isOpen,
    };
  }

  private get elementChildClass(): { [className: string]: boolean } {
    return {
      'spw-accordion-item__trigger': true,
    };
  }

  render() {
    return (
      <Host class={this.elementClass}>
        <div class="spw-accordion-item__content">
          <a href="#" class={this.elementChildClass} onClick={event => this.handleClick(event)}>
            <slot name="title"></slot>
          </a>
          <slot name="content"></slot>
        </div>
      </Host>
    );
  }
}
